import React, { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import styled from 'styled-components';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhoneSlash, FaCog, FaColumns, FaExpand } from 'react-icons/fa';
import { Button, ButtonVariant, ButtonSize } from '../atoms/button';
import Text, { TextSize, FontWeight, TextAlign } from '../atoms/text';
import Spinner, { SpinnerSize, SpinnerVariant } from '../atoms/spinner';

const VideoCallContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  color: #192947;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  border-bottom: 1px solid #e0e8f5;
`;

const CallInfo = styled.div`
  text-align: center;
  flex: 1;
`;

const VideoContainer = styled.div`
  position: relative;
  background-color: #17243e;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ mode }) => mode === 'pip' ? 'auto' : '80%'};
  width: ${({ mode }) => mode === 'pip' ? '60%' : '100%'};
  max-width: ${({ mode }) => mode === 'pip' ? '80vw' : 'none'};
  max-height: ${({ mode }) => mode === 'pip' ? '75vh' : 'none'};
  margin: ${({ mode }) => mode === 'pip' ? '0 auto' : '0'};

  @media (max-width: 600px) {
    ${({ mode }) => mode === 'pip' && `
      width: 95vw;
      max-width: 100vw;
      min-height: 60vh;
    `}
  }
`;

const SplitViewContainer = styled.div`
  display: flex;
  gap: 20px;
  height: 80%;
  flex-direction: row;
  @media (max-width: 600px) {
    flex-direction: column;
    height: auto;
  }
`;

const PictureInPictureContainer = styled.div`
  position: relative;
  height: 80%;
  width: 100%;
`;

const SmallVideoContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 200px;
  height: 150px;
  background-color: #1d2c4a;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e0e8f5;
  z-index: 10;
`;

const ViewModeControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// ViewModeButton replaced with Button atom

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const VideoLabel = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(23, 36, 62, 0.56);
  padding: 6px 12px;
  border-radius: 16px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;

  @media (max-width: 600px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 70px;
    z-index: 100;
    padding: 12px 0;
    border-radius: 16px 16px 0 0;
    background: rgba(255, 255, 255, 0.9);
    justify-content: center;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-direction: column;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  background-color: rgba(229, 62, 62, 0.1);
  border: 1px solid rgba(229, 62, 62, 0.3);
  padding: 16px;
  border-radius: 8px;
  margin: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const NavigationWarningOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 36, 62, 0.56);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;

const NavigationWarningDialog = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border: 1px solid #e0e8f5;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  margin: 20px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(39, 68, 131, 0.14);
`;

const WarningTitle = styled.h3`
  color: #192947;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const WarningMessage = styled.p`
  color: #455776;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const WarningButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const VideoCall = ({ 
  appId, 
  channelName, 
  token, 
  uid, 
  onCallEnd, 
  expertName = "Expert",
  onError,
  threadId
}) => {
  const [client, setClient] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isTestMode, setIsTestMode] = useState(false);
  const [viewMode, setViewMode] = useState('split'); // 'split' or 'pip'
  const [wasVideoEnabledBeforeHide, setWasVideoEnabledBeforeHide] = useState(true);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const callTimerRef = useRef(null);

  // Test mode support
  useEffect(() => {
    if (appId === 'test-app-id' && token === 'test-token') {
      setIsTestMode(true);
      setIsConnected(true);
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
  }, [appId, token]);

  // Initialize Agora client and set up event handlers
  useEffect(() => {
    if (isTestMode) return;
    const initClient = async () => {
      try {
        if (!AgoraRTC) {
          throw new Error('Agora SDK not loaded. Please check if agora-rtc-sdk-ng is installed.');
        }
        const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        
        // Set up event handlers BEFORE joining channel
        const handleUserPublished = async (user, mediaType) => {
          console.log('User published:', user.uid, mediaType);
          try {
            await agoraClient.subscribe(user, mediaType);
            if (mediaType === 'video') {
              setRemoteUsers(prev => {
                const existing = prev.find(u => u.uid === user.uid);
                if (!existing) {
                  return [...prev, user];
                }
                return prev;
              });
            }
            if (mediaType === 'audio') {
              user.audioTrack?.play();
            }
          } catch (subscribeError) {
            console.error('Error subscribing to user:', subscribeError);
          }
        };
        
        const handleUserUnpublished = (user) => {
          console.log('User unpublished:', user.uid);
          setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
        };
        
        const handleUserLeft = (user) => {
          console.log('User left:', user.uid);
          setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
        };
        
        // Register event handlers
        agoraClient.on('user-published', handleUserPublished);
        agoraClient.on('user-unpublished', handleUserUnpublished);
        agoraClient.on('user-left', handleUserLeft);
        
        setClient(agoraClient);
      } catch (err) {
        setError(`Failed to initialize video call client: ${err.message}`);
        if (onError) onError(err);
      }
    };
    initClient();
    return () => {
      if (client) {
        client.removeAllListeners();
      }
    };
  }, [isTestMode]);

  // Event handlers are now set up in client initialization above

  // Connect to channel
  useEffect(() => {
    if (isTestMode) return;
    const connectToChannel = async () => {
      if (!client || !appId || !channelName || !token) return;
      setIsConnecting(true);
      setError(null);
      try {
        // Check for media permissions first
        try {
          await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch (mediaErr) {
          throw new Error(`Camera/microphone access denied: ${mediaErr.message}`);
        }
        
        console.log('Joining channel with client:', client);
        await client.join(appId, channelName, token, uid);
        console.log('Successfully joined channel');
        
        setIsConnected(true);
        setIsConnecting(false);
        
        // Create tracks with retry mechanism
        let audioTrack, videoTrack;
        try {
          [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
          console.log('Successfully created audio and video tracks');
        } catch (trackError) {
          console.error('Error creating tracks:', trackError);
          throw new Error(`Failed to create media tracks: ${trackError.message}`);
        }
        
        // Set tracks in state
        setLocalAudioTrack(audioTrack);
        setLocalVideoTrack(videoTrack);
        
        // Ensure tracks are enabled by default and sync state
        await audioTrack.setMuted(false);
        await videoTrack.setEnabled(true);
        
        // Sync component state with actual track state
        setIsAudioEnabled(true);
        setIsVideoEnabled(true);
        setIsAudioMuted(false);
        
        // Publish tracks
        try {
          await client.publish([audioTrack, videoTrack]);
          console.log('Successfully published tracks');
        } catch (publishError) {
          console.error('Error publishing tracks:', publishError);
          throw new Error(`Failed to publish tracks: ${publishError.message}`);
        }
        
        // Play local video with retry
        const playLocalVideo = () => {
          if (localVideoRef.current && videoTrack) {
            try {
              videoTrack.play(localVideoRef.current);
              console.log('Local video started playing');
            } catch (playError) {
              console.error('Error playing local video:', playError);
              // Retry once after a short delay
              setTimeout(() => {
                try {
                  videoTrack.play(localVideoRef.current);
                } catch (retryError) {
                  console.error('Retry failed for local video:', retryError);
                }
              }, 500);
            }
          }
        };
        
        // Use timeout to ensure DOM is ready
        setTimeout(playLocalVideo, 100);
        
        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
        
      } catch (err) {
        console.error('Connection error:', err);
        setError(`Failed to connect to video call: ${err.message}`);
        setIsConnecting(false);
        if (onError) onError(err);
      }
    };
    connectToChannel();
  }, [client, appId, channelName, token, uid, isTestMode]);

  // Handle remote video elements with improved error handling
  useEffect(() => {
    const attachRemoteVideos = async () => {
      for (const user of remoteUsers) {
        if (!user.videoTrack) continue;
        
        const videoEl = remoteVideoRefs.current[user.uid];
        if (videoEl) {
          try {
            // Stop previous playback if any
            user.videoTrack.stop();
            console.log(`Attaching remote video for user ${user.uid}`);
            
            // Use a timeout to ensure the DOM is updated before playing
            setTimeout(() => {
              try {
                user.videoTrack.play(videoEl);
                console.log(`Successfully playing remote video for user ${user.uid}`);
              } catch (playError) {
                console.error(`Error playing remote video for user ${user.uid}:`, playError);
                // Retry once
                setTimeout(() => {
                  try {
                    user.videoTrack.play(videoEl);
                  } catch (retryError) {
                    console.error(`Retry failed for remote video user ${user.uid}:`, retryError);
                  }
                }, 300);
              }
            }, 100);
          } catch (error) {
            console.error(`Error handling remote video for user ${user.uid}:`, error);
          }
        }
      }
    };
    
    attachRemoteVideos();
    
    // Clean up old refs for users that have left
    Object.keys(remoteVideoRefs.current).forEach(uid => {
      if (!remoteUsers.find(u => String(u.uid) === String(uid))) {
        remoteVideoRefs.current[uid] = null;
      }
    });
  }, [remoteUsers, viewMode]);

  // Handle local video playback when view mode changes or track updates
  useEffect(() => {
    const playLocalVideo = () => {
      if (localVideoTrack && localVideoRef.current) {
        try {
          console.log('Playing local video track');
          localVideoTrack.play(localVideoRef.current);
        } catch (playError) {
          console.error('Error playing local video:', playError);
          // Retry after a short delay
          setTimeout(() => {
            try {
              if (localVideoTrack && localVideoRef.current) {
                localVideoTrack.play(localVideoRef.current);
              }
            } catch (retryError) {
              console.error('Retry failed for local video:', retryError);
            }
          }, 200);
        }
      }
    };
    
    // Use timeout to ensure DOM is ready
    setTimeout(playLocalVideo, 50);
  }, [localVideoTrack, viewMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, []);

  // Helper function to stop all media streams
  const stopAllMediaStreams = async () => {
    try {
      // Get all active media streams
      const streams = await navigator.mediaDevices.enumerateDevices();
      
      // Additional cleanup - try to get any active streams and stop them
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // This is a workaround to force release of all media devices
        const dummyStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: false })
          .catch(() => null);
        
        if (dummyStream) {
          dummyStream.getTracks().forEach(track => {
            track.stop();
          });
        }
      }
    } catch (err) {
      console.log('Error in stopAllMediaStreams:', err);
    }
  };

  // Handle navigation warning for mobile video calls
  useEffect(() => {
    // Only add navigation warning on mobile during active video calls
    if (window.innerWidth <= 768 && isConnected && !isTestMode) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'You are currently in a video call. Leaving this page will end the call.';
        return 'You are currently in a video call. Leaving this page will end the call.';
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Show warning popup when page becomes hidden on mobile
          setShowNavigationWarning(true);
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [isConnected, isTestMode]);

  // Cleanup on actual page unload (not navigation within app)
  useEffect(() => {
    const handlePageHide = async (e) => {
      if (localAudioTrack) {
        try {
          await localAudioTrack.setEnabled(false);
          localAudioTrack.stop();
          localAudioTrack.close();
        } catch (err) {
          console.error('Error closing audio track on unload:', err);
        }
      }
      if (localVideoTrack) {
        try {
          await localVideoTrack.setEnabled(false);
          localVideoTrack.stop();
          localVideoTrack.close();
        } catch (err) {
          console.error('Error closing video track on unload:', err);
        }
      }
      if (client) {
        try {
          await client.leave();
        } catch (err) {
          console.error('Error leaving channel on unload:', err);
        }
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      
      // Force stop all media streams
      await stopAllMediaStreams();
    };
    
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [localAudioTrack, localVideoTrack, client]);

  const toggleAudio = async () => {
    if (localAudioTrack) {
      try {
        const newMutedState = !isAudioMuted;
        console.log(`Setting audio muted to: ${newMutedState}`);
        
        // Use setMuted instead of setEnabled to prevent affecting other tracks
        await localAudioTrack.setMuted(newMutedState);
        setIsAudioMuted(newMutedState);
        
        // Update isAudioEnabled to reflect the actual audio state (opposite of muted)
        setIsAudioEnabled(!newMutedState);
        
        console.log(`Audio toggle successful: muted=${newMutedState}, enabled=${!newMutedState}`);
      } catch (error) {
        console.error('Error toggling audio:', error);
        // Fallback to setEnabled if setMuted fails
        try {
          const newEnabledState = !isAudioEnabled;
          await localAudioTrack.setEnabled(newEnabledState);
          setIsAudioEnabled(newEnabledState);
          setIsAudioMuted(!newEnabledState);
          console.log(`Fallback audio toggle: enabled=${newEnabledState}`);
        } catch (fallbackError) {
          console.error('Error with fallback audio toggle:', fallbackError);
        }
      }
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      try {
        if (isVideoEnabled) {
          await localVideoTrack.setEnabled(false);
          setIsVideoEnabled(false);
        } else {
          await localVideoTrack.setEnabled(true);
          setIsVideoEnabled(true);
          // Re-attach the video track to the video element after enabling
          // Use a small delay to ensure the track is ready
          setTimeout(() => {
            if (localVideoRef.current && localVideoTrack) {
              try {
                localVideoTrack.play(localVideoRef.current);
              } catch (playError) {
                console.error('Error playing video track:', playError);
              }
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error toggling video:', error);
      }
    }
  };

  // Recovery function to recreate video track if it becomes corrupted
  const recoverVideoTrack = async () => {
    if (!client) return false;
    
    try {
      console.log('Attempting to recover video track...');
      
      // Stop and close the current video track
      if (localVideoTrack) {
        try {
          await localVideoTrack.setEnabled(false);
          localVideoTrack.stop();
          localVideoTrack.close();
        } catch (err) {
          console.error('Error stopping corrupted video track:', err);
        }
      }
      
      // Create a new video track
      const newVideoTrack = await AgoraRTC.createCameraVideoTrack();
      
      // Update state
      setLocalVideoTrack(newVideoTrack);
      setIsVideoEnabled(true);
      
      // Publish the new track
      await client.publish([newVideoTrack]);
      
      // Play the new track
      if (localVideoRef.current) {
        newVideoTrack.play(localVideoRef.current);
      }
      
      console.log('Video track recovered successfully');
      return true;
    } catch (error) {
      console.error('Failed to recover video track:', error);
      return false;
    }
  };

  // Handle navigation warning responses
  const handleStayInCall = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    console.log('User chose to stay in video call');
  };

  const handleLeaveCall = async () => {
    setShowNavigationWarning(false);
    console.log('User chose to leave video call');
    await endCall();
  };

  const endCall = async () => {
    try {
      if (isTestMode) {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
        setIsConnected(false);
        if (onCallEnd) {
          onCallEnd(callDuration);
        }
        return;
      }
      
      // First, unpublish tracks if they're published
      if (client && (localAudioTrack || localVideoTrack)) {
        try {
          console.log('Unpublishing local tracks');
          await client.unpublish([localAudioTrack, localVideoTrack].filter(track => track));
        } catch (unpublishErr) {
          console.error('Error unpublishing tracks:', unpublishErr);
        }
      }
      
      // Stop and close local audio track
      if (localAudioTrack) {
        console.log('Stopping local audio track');
        try {
          // First disable the track
          await localAudioTrack.setEnabled(false);
          // Then stop it
          localAudioTrack.stop();
          // Finally close it to release resources
          localAudioTrack.close();
          
          // Get the MediaStreamTrack and stop it directly for immediate release
          const audioMediaTrack = localAudioTrack.getMediaStreamTrack();
          if (audioMediaTrack) {
            audioMediaTrack.stop();
          }
        } catch (audioErr) {
          console.error('Error stopping audio track:', audioErr);
        }
      }
      
      // Stop and close local video track
      if (localVideoTrack) {
        console.log('Stopping local video track');
        try {
          // First disable the track
          await localVideoTrack.setEnabled(false);
          // Then stop it
          localVideoTrack.stop();
          // Finally close it to release resources
          localVideoTrack.close();
          
          // Get the MediaStreamTrack and stop it directly for immediate release
          const videoMediaTrack = localVideoTrack.getMediaStreamTrack();
          if (videoMediaTrack) {
            videoMediaTrack.stop();
          }
        } catch (videoErr) {
          console.error('Error stopping video track:', videoErr);
        }
      }
      
      // Stop any playing remote tracks
      remoteUsers.forEach(user => {
        try {
          if (user.audioTrack) {
            user.audioTrack.stop();
          }
          if (user.videoTrack) {
            user.videoTrack.stop();
          }
        } catch (remoteErr) {
          console.error('Error stopping remote track:', remoteErr);
        }
      });
      
      // Clear video elements
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      Object.values(remoteVideoRefs.current).forEach(videoEl => {
        if (videoEl) {
          videoEl.srcObject = null;
        }
      });
      
      // Leave the channel
      if (client) {
        console.log('Leaving channel');
        try {
          await client.leave();
          // Remove all event listeners
          client.removeAllListeners();
        } catch (leaveErr) {
          console.error('Error leaving channel:', leaveErr);
        }
      }
      
      // Clear the timer
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      
      // Reset all state variables
      setIsConnected(false);
      setIsAudioEnabled(true);
      setIsVideoEnabled(true);
      setIsAudioMuted(false);
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setRemoteUsers([]);
      setClient(null);
      
      // Force garbage collection by nullifying references
      localVideoRef.current = null;
      remoteVideoRefs.current = {};
      
      // Additional cleanup - force stop all media streams
      await stopAllMediaStreams();
      
      console.log('Call ended successfully, all resources cleaned up');
      
      // Small delay to ensure cleanup completes before navigation
      setTimeout(() => {
        if (onCallEnd) {
          onCallEnd(callDuration);
        }
      }, 100);
      
    } catch (err) {
      console.error('Error ending call:', err);
      // Still try to call onCallEnd even if there's an error
      if (onCallEnd) {
        onCallEnd(callDuration);
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // View mode icons now handled by Icon atom

  const changeViewMode = (mode) => {
    setViewMode(mode);
  };

  if (error) {
    return (
      <VideoCallContainer>
        <ErrorMessage>
          <Text color="#c53030" align={TextAlign.CENTER}>
            {error}
          </Text>
          <Button 
            variant={ButtonVariant.DANGER} 
            size={ButtonSize.SM}
            onClick={() => window.location.reload()}
            style={{ marginTop: '10px' }}
          >
            Retry
          </Button>
        </ErrorMessage>
      </VideoCallContainer>
    );
  }

  return (
    <VideoCallContainer>
      <Header>
        <div></div>
        <CallInfo>
          <Text 
            as="h2" 
            size={TextSize.LG} 
            weight={FontWeight.MEDIUM} 
            color="#192947"
            align={TextAlign.CENTER}
          >
            Call with {expertName}
          </Text>
          <Text 
            size={TextSize.SM} 
            color="#455776"
            align={TextAlign.CENTER}
          >
            {isConnecting ? 'Connecting...' : isConnected ? `Connected - ${formatDuration(callDuration)}` : 'Disconnected'}
          </Text>
        </CallInfo>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <ViewModeControls>
            <Button
              variant={viewMode === 'split' ? ButtonVariant.VIEW_MODE_ACTIVE : ButtonVariant.VIEW_MODE_INACTIVE}
              size={ButtonSize.SM}
              onClick={() => changeViewMode('split')}
              style={{
                width: '36px',
                height: '36px',
                padding: '8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}
              aria-label="Split View"
            >
              <FaColumns />
            </Button>
            <Button
              variant={viewMode === 'pip' ? ButtonVariant.VIEW_MODE_ACTIVE : ButtonVariant.VIEW_MODE_INACTIVE}
              size={ButtonSize.SM}
              onClick={() => changeViewMode('pip')}
              style={{
                width: '36px',
                height: '36px',
                padding: '8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}
              aria-label="Picture in Picture"
            >
              <FaExpand />
            </Button>
          </ViewModeControls>
        </div>
      </Header>

      {/* Render based on view mode */}
      {viewMode === 'split' && (
        <SplitViewContainer>
          {/* Expert video first (left on desktop, top on mobile) */}
          {remoteUsers.length > 0 ? (
            remoteUsers.map(user => (
              <VideoContainer key={user.uid} style={{ flex: 1 }}>
                <VideoElement 
                  ref={el => remoteVideoRefs.current[user.uid] = el}
                  autoPlay 
                />
                <VideoLabel>
                  <Text size={TextSize.XS} weight={FontWeight.MEDIUM} color="white">
                    {expertName}
                  </Text>
                </VideoLabel>
              </VideoContainer>
            ))
          ) : (
            <VideoContainer style={{ flex: 1 }}>
              <LoadingContainer>
                <Spinner variant={SpinnerVariant.WHITE} size={SpinnerSize.MD} />
                <Text 
                  size={TextSize.MD} 
                  color="#65748d"
                  align={TextAlign.CENTER}
                >
                  {isConnecting ? 'Connecting to expert...' : 'Waiting for expert to join...'}
                </Text>
              </LoadingContainer>
            </VideoContainer>
          )}
          {/* User video second (right on desktop, bottom on mobile) */}
          <VideoContainer style={{ flex: 1 }}>
            <VideoElement ref={localVideoRef} autoPlay muted />
            <VideoLabel>
              <Text size={TextSize.XS} weight={FontWeight.MEDIUM} color="white">
                You
              </Text>
            </VideoLabel>
          </VideoContainer>
        </SplitViewContainer>
      )}

      {/* Picture in picture view mode */}
      {viewMode === 'pip' && (
        <PictureInPictureContainer>
          {/* Main video (expert) */}
          {remoteUsers.length > 0 ? (
            remoteUsers.map(user => (
              <VideoContainer key={user.uid} mode="pip">
                <VideoElement 
                  ref={el => remoteVideoRefs.current[user.uid] = el}
                  autoPlay 
                />
                <VideoLabel>
                  <Text size={TextSize.XS} weight={FontWeight.MEDIUM} color="white">
                    {expertName}
                  </Text>
                </VideoLabel>
              </VideoContainer>
            ))
          ) : (
            <VideoContainer mode="pip">
              <LoadingContainer>
                <Spinner variant={SpinnerVariant.WHITE} size={SpinnerSize.MD} />
                <Text 
                  size={TextSize.MD} 
                  color="#65748d"
                  align={TextAlign.CENTER}
                >
                  {isConnecting ? 'Connecting to expert...' : 'Waiting for expert to join...'}
                </Text>
              </LoadingContainer>
            </VideoContainer>
          )}
          {/* Small overlay video (user) */}
          <SmallVideoContainer>
            <VideoElement ref={localVideoRef} autoPlay muted />
            <VideoLabel style={{ 
              padding: '4px 8px',
              bottom: '8px',
              left: '8px'
            }}>
              <Text size={TextSize.XS} weight={FontWeight.MEDIUM} color="white">
                You
              </Text>
            </VideoLabel>
          </SmallVideoContainer>
        </PictureInPictureContainer>
      )}

      <Controls>
        <Button
          variant={isAudioEnabled ? ButtonVariant.CONTROL_ACTIVE : ButtonVariant.CONTROL_INACTIVE}
          onClick={toggleAudio}
          disabled={isTestMode}
        >
          {isAudioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
        </Button>
        <Button
          variant={isVideoEnabled ? ButtonVariant.CONTROL_ACTIVE : ButtonVariant.CONTROL_INACTIVE}
          onClick={toggleVideo}
          disabled={isTestMode}
        >
          {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
        </Button>
        {/* Add recovery button if video is not working */}
        {isConnected && !isVideoEnabled && (
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={recoverVideoTrack}
            size={ButtonSize.SM}
            style={{ fontSize: '12px', padding: '8px 12px' }}
          >
            Restore Video
          </Button>
        )}
        <Button
          variant={ButtonVariant.CONTROL_DANGER}
          onClick={endCall}
        >
          <FaPhoneSlash />
        </Button>
      </Controls>

      {/* Navigation Warning Popup (Mobile Only) */}
      {showNavigationWarning && window.innerWidth <= 768 && (
        <NavigationWarningOverlay>
          <NavigationWarningDialog>
            <WarningTitle>Video Call Active</WarningTitle>
            <WarningMessage>
              You are currently in a video call. Navigating away will end the call and disconnect you from the conversation.
            </WarningMessage>
            <WarningButtons>
              <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SM}
                onClick={handleStayInCall}
              >
                Stay in Call
              </Button>
              <Button
                variant={ButtonVariant.DANGER}
                size={ButtonSize.SM}
                onClick={handleLeaveCall}
              >
                End Call
              </Button>
            </WarningButtons>
          </NavigationWarningDialog>
        </NavigationWarningOverlay>
      )}
    </VideoCallContainer>
  );
};

export default VideoCall;

